// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

export interface Notification {
  id: string;
  user_id: string;
  username?: string;
  subject: string;
  content: string;
  code: number;
  sender_id?: string;
  create_time: string;
  persistent: boolean;
}

export interface NotificationList {
  notifications: Notification[];
  cursor?: string;
  cacheable_cursor?: string;
}

export interface NotificationSearchFilters {
  user_id?: string;
  username?: string;
  subject?: string;
  content?: string;
  code?: number;
  sender_id?: string;
  persistent?: boolean;
  limit?: number;
}

export interface SendNotificationRequest {
  user_id: string;
  subject: string;
  content: string;
  code?: number;
  sender_id?: string;
  persistent?: boolean;
}

export interface SendNotificationBatchRequest {
  user_ids: string[];
  subject: string;
  content: string;
  code?: number;
  sender_id?: string;
  persistent?: boolean;
}

export interface DeleteNotificationRequest {
  notification_id: string;
  user_id: string;
}

export interface DeleteNotificationBatchRequest {
  user_id: string;
  notification_ids?: string[];
}

export enum NotificationCode {
  FRIEND_REQUEST = 0,
  FRIEND_ACCEPT = 1,
  FRIEND_REJECT = 2,
  FRIEND_REMOVE = 3,
  GROUP_ADD = 4,
  GROUP_JOIN_REQUEST = 5,
  GROUP_JOIN_ACCEPT = 6,
  GROUP_JOIN_REJECT = 7,
  GROUP_KICK = 8,
  GROUP_PROMOTE = 9,
  GROUP_DEMOTE = 10,
  GROUP_BAN = 11,
  CHAT_MESSAGE = 12,
  CHAT_CHANNEL_UPDATE = 13,
  LEADERBOARD_RECORD = 14,
  TOURNAMENT_START = 15,
  TOURNAMENT_END = 16,
  CUSTOM = 100
}

export interface NotificationTemplate {
  id: string;
  name: string;
  subject: string;
  content: string;
  code: number;
  persistent: boolean;
  create_time: string;
  update_time: string;
}

export interface NotificationTemplateList {
  templates: NotificationTemplate[];
  cursor?: string;
}

export interface CreateNotificationTemplateRequest {
  name: string;
  subject: string;
  content: string;
  code?: number;
  persistent?: boolean;
}

export interface UpdateNotificationTemplateRequest {
  template_id: string;
  name?: string;
  subject?: string;
  content?: string;
  code?: number;
  persistent?: boolean;
}

export interface DeleteNotificationTemplateRequest {
  template_id: string;
}

export interface NotificationStats {
  total_notifications: number;
  total_users_with_notifications: number;
  notifications_by_code: { [code: number]: number };
  recent_activity: {
    date: string;
    count: number;
  }[];
}
